import { useMemo } from "react";
import { createUseStyles } from "react-jss";
import clsx from "clsx";

import { STATUS_OPTIONS } from "./imageFilter";

/**
 * FiltersPanel — the "Filters" controls of the studio inspector (WP-E).
 *
 * It's a thin, controlled UI over the filter state that lives in NodeEditor:
 *   - Status checkboxes: Placed / Linked / Unlinked / Unplaced
 *   - Floor / Area dropdown populated from tour.data.floorplans
 *   - Tag checkboxes derived from the board images (mirrors Filter.jsx)
 *
 * It never applies the filter itself — TourCanvas (canvas) and StudioInspector
 * (minimap) read the same filter through the shared `matchesStudioFilter`
 * helper, so the panel only reads/writes the `{ status, floor, tags }` shape.
 */
const FiltersPanel = ({ filter, setFilter, images = [], floorplans = [] }) => {
  const classes = useStyles();

  const status = filter?.status ?? [];
  const tags = filter?.tags ?? [];
  const floor = filter?.floor ?? null;

  const availableTags = useMemo(
    () => [...new Set(images.map(image => image.tag ?? "Untagged"))].sort(),
    [images],
  );

  const update = patch => setFilter({ ...filter, ...patch });

  const toggle = (key, value) => {
    const current = filter?.[key] ?? [];
    update({
      [key]: current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value],
    });
  };

  const hasActiveFilter =
    status.length > 0 || tags.length > 0 || (floor != null && floor !== "");

  return (
    <div className={classes.root}>
      <div className={classes.group}>
        <div className={classes.groupLabel}>Status</div>
        {STATUS_OPTIONS.map(option => (
          <label key={option.value} className={classes.check}>
            <input
              type="checkbox"
              className={classes.checkbox}
              checked={status.includes(option.value)}
              onChange={() => toggle("status", option.value)}
            />
            {option.label}
          </label>
        ))}
      </div>

      <div className={classes.group}>
        <div className={classes.groupLabel}>Floor / Area</div>
        <select
          className={classes.select}
          value={floor ?? ""}
          onChange={e => update({ floor: e.target.value || null })}
        >
          <option value="">All floors</option>
          {floorplans.map((floorplan, index) => (
            <option key={floorplan.uuid ?? index} value={floorplan.uuid}>
              {floorplan.name || `Floor ${index + 1}`}
            </option>
          ))}
        </select>
        {floorplans.length === 0 && (
          <div className={classes.hint}>No floor plans for this tour.</div>
        )}
      </div>

      <div className={classes.group}>
        <div className={classes.groupLabel}>Tags</div>
        {availableTags.length === 0 ? (
          <div className={classes.hint}>No tagged scenes.</div>
        ) : (
          availableTags.map(tag => (
            <label key={tag} className={classes.check}>
              <input
                type="checkbox"
                className={classes.checkbox}
                checked={tags.includes(tag)}
                onChange={() => toggle("tags", tag)}
              />
              {tag}
            </label>
          ))
        )}
      </div>

      <button
        type="button"
        className={clsx(classes.clear, {
          [classes.clearDisabled]: !hasActiveFilter,
        })}
        onClick={() => setFilter({ status: [], floor: null, tags: [] })}
        disabled={!hasActiveFilter}
      >
        Clear filters
      </button>
    </div>
  );
};

const useStyles = createUseStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing2,
  },
  group: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing1,
  },
  groupLabel: {
    color: theme.colorLightGrey,
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  check: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing1,
    color: theme.textColor,
    fontSize: 13,
    cursor: "pointer",
  },
  checkbox: {
    margin: 0,
    cursor: "pointer",
    accentColor: theme.colorPrimary,
  },
  select: {
    width: "100%",
    boxSizing: "border-box",
    padding: [7, 8],
    fontSize: 13,
    color: theme.textColor,
    backgroundColor: theme.colorBlack,
    border: `1px solid ${theme.colorDarkGrey}`,
    borderRadius: theme.borderRadius,
    outline: "none",
    cursor: "pointer",
    "&:focus": {
      borderColor: theme.colorPrimary,
    },
  },
  hint: {
    color: theme.colorGrey,
    fontSize: 12,
  },
  clear: {
    marginTop: theme.spacing1,
    padding: [6, 8],
    fontSize: 12,
    color: theme.textColor,
    backgroundColor: "transparent",
    border: `1px solid ${theme.colorDarkGrey}`,
    borderRadius: theme.borderRadius,
    cursor: "pointer",
    transitionProperty: "color, border-color",
    transitionDuration: theme.transitionDuration,
    "&:hover": {
      borderColor: theme.colorPrimary,
      color: theme.colorPrimary,
    },
  },
  clearDisabled: {
    opacity: 0.4,
    cursor: "default",
    "&:hover": {
      borderColor: theme.colorDarkGrey,
      color: theme.textColor,
    },
  },
}));

export default FiltersPanel;
